import { useCallback, useEffect, useRef, useState } from 'react';
import type { CreateJobRequest, Job, JobLikenessRating } from '@volt/shared';
import { useSession } from '../state/session.js';
import {
  hasActiveJobs,
  isUnauthorizedError,
  mergeJobsHead,
  pollDelayMs,
} from '../lib/jobsPolling.js';

const PAGE_SIZE = 24;

export interface JobsState {
  jobs: Job[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  generating: boolean;
  create: (body: CreateJobRequest) => Promise<Job>;
  reload: () => Promise<void>;
  loadMore: () => Promise<void>;
  toggleFavorite: (jobId: string, isFavorite: boolean) => Promise<void>;
  /** "Looks like me" / "does not look like me" on a character result. */
  rateLikeness: (jobId: string, rating: JobLikenessRating | null) => Promise<void>;
  removeJob: (jobId: string) => Promise<void>;
  /** Stops a queued / running job; the reserve is refunded. */
  cancelJob: (jobId: string) => Promise<void>;
}

/**
 * Loads the first page of async generation jobs when the screen opens (and again when
 * it becomes visible), then polls it only while a job is pending or processing, backing
 * off exponentially after errors and stopping for good on a 401. Polling only ever
 * refreshes the "head" of the list (the most recent jobs), so it never resets scroll
 * position or discards jobs already paged into view; unchanged jobs keep their objects.
 */
export function useJobs(active: boolean): JobsState {
  const { api } = useSession();
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [errorCount, setErrorCount] = useState(0);
  const mounted = useRef(true);
  const cursorRef = useRef<string | null>(null);
  const initializedRef = useRef(false);
  const deletedIdsRef = useRef<Set<string>>(new Set());
  /** Sequence number of the latest head request: older responses are ignored. */
  const requestSeqRef = useRef(0);
  /** Set on 401: the session is gone, polling would only produce more 401s. */
  const stoppedRef = useRef(false);

  const load = useCallback(async () => {
    if (stoppedRef.current) return;
    const seq = ++requestSeqRef.current;
    try {
      const response = await api.listJobs({ limit: PAGE_SIZE });
      if (!mounted.current || seq !== requestSeqRef.current) return;
      setJobs((prev) => mergeJobsHead(prev, response.jobs, deletedIdsRef.current));
      setErrorCount(0);
      if (!initializedRef.current) {
        initializedRef.current = true;
        cursorRef.current = response.nextCursor;
        setHasMore(response.hasMore);
        setInitialized(true);
      }
    } catch (error) {
      if (!mounted.current || seq !== requestSeqRef.current) return;
      if (isUnauthorizedError(error)) stoppedRef.current = true;
      setErrorCount((count) => count + 1);
      /* keep previous jobs */
    } finally {
      if (mounted.current && seq === requestSeqRef.current) setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // One fetch when the screen opens or the section changes, and again when the
  // app comes back to the foreground (a job may have finished meanwhile).
  useEffect(() => {
    if (!active) return;
    stoppedRef.current = false;
    void load();
    const onVisible = () => {
      if (document.visibilityState === 'visible') void load();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [active, load]);

  // Polling only while something is still running (or until the first page has
  // loaded at all), with exponential backoff after failures.
  const polling = active && (hasActiveJobs(jobs) || (!initialized && errorCount > 0));
  useEffect(() => {
    if (!polling || stoppedRef.current) return;
    let cancelled = false;
    let timer: number | undefined;
    const schedule = () => {
      timer = window.setTimeout(() => {
        if (cancelled || stoppedRef.current) return;
        const run = document.visibilityState === 'hidden' ? Promise.resolve() : load();
        void run.finally(() => {
          if (!cancelled) schedule();
        });
      }, pollDelayMs(errorCount));
    };
    schedule();
    return () => {
      cancelled = true;
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [polling, errorCount, load]);

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore || !cursorRef.current) return;
    setLoadingMore(true);
    try {
      const response = await api.listJobs({ limit: PAGE_SIZE, cursor: cursorRef.current });
      if (mounted.current) {
        setJobs((prev) => {
          const existingIds = new Set(prev.map((job) => job.id));
          const additions = response.jobs.filter(
            (job) => !existingIds.has(job.id) && !deletedIdsRef.current.has(job.id),
          );
          return [...prev, ...additions];
        });
      }
      cursorRef.current = response.nextCursor;
      if (mounted.current) setHasMore(response.hasMore);
    } catch {
      /* keep previous cursor so the sentinel can retry */
    } finally {
      if (mounted.current) setLoadingMore(false);
    }
  }, [api, hasMore, loadingMore]);

  const create = useCallback(
    async (body: CreateJobRequest) => {
      setGenerating(true);
      try {
        const job = await api.createJob(body);
        if (mounted.current) setJobs((prev) => [job, ...prev]);
        return job;
      } finally {
        if (mounted.current) setGenerating(false);
      }
    },
    [api],
  );

  const toggleFavorite = useCallback(
    async (jobId: string, isFavorite: boolean) => {
      const updated = await api.toggleJobFavorite(jobId, isFavorite);
      if (mounted.current) {
        setJobs((prev) => prev.map((job) => (job.id === jobId ? updated : job)));
      }
    },
    [api],
  );

  const rateLikeness = useCallback(
    async (jobId: string, rating: JobLikenessRating | null) => {
      const updated = await api.rateJobLikeness(jobId, rating);
      if (mounted.current) {
        setJobs((prev) => prev.map((job) => (job.id === jobId ? updated : job)));
      }
    },
    [api],
  );

  const cancelJob = useCallback(
    async (jobId: string) => {
      const updated = await api.cancelJob(jobId);
      if (mounted.current) {
        setJobs((prev) => prev.map((job) => (job.id === jobId ? updated : job)));
      }
    },
    [api],
  );

  const removeJob = useCallback(
    async (jobId: string) => {
      deletedIdsRef.current.add(jobId);
      let removed: Job | undefined;
      if (mounted.current) {
        setJobs((prev) => {
          removed = prev.find((job) => job.id === jobId);
          return prev.filter((job) => job.id !== jobId);
        });
      }
      try {
        await api.deleteJob(jobId);
      } catch (error) {
        deletedIdsRef.current.delete(jobId);
        if (mounted.current && removed) {
          const restored = removed;
          setJobs((prev) => (prev.some((job) => job.id === jobId) ? prev : [...prev, restored]));
        }
        throw error;
      }
    },
    [api],
  );

  return {
    jobs,
    loading,
    loadingMore,
    hasMore,
    generating,
    create,
    reload: load,
    loadMore,
    toggleFavorite,
    rateLikeness,
    removeJob,
    cancelJob,
  };
}
